import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getTranslation,
  Loader,
  TranslationProvider,
} from '@resy/ui-kitchen';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers';
import { IDLE } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import PrepayStripe from './PrepayStripe';
import './PrepayPayment.scss';

const prepayPaymentPropTypes = {
  $location: PropTypes.shape({
    url: PropTypes.func.isRequired,
  }).isRequired,
  $rootScope: PropTypes.shape({
    matchMedia: PropTypes.shape(PropTypes.object.isRequired),
  }).isRequired,
  $routeParams: PropTypes.shape({
    token: PropTypes.string.isRequired,
  }).isRequired,
  AutomationMode: PropTypes.bool.isRequired,
  Prepay: PropTypes.shape({
    get: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
  }).isRequired,
  configStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  stripePublishableKey: PropTypes.string,
};

const PrepayPayment = ({
  $routeParams, $location, $rootScope, Prepay, AutomationMode, dispatch, configStatus, stripePublishableKey,
}) => {
  const [prepayData, setPrepayData] = useState({});
  const [errorMessages, setErrorMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const { token } = $routeParams;

  useEffect(() => {
    const getPrepayData = async () => {
      try {
        const data = await Prepay.get(token);
        if (data.is_editable === 0) {
          $location.url(`/prepay/${token}/confirmation`);
        }
        setPrepayData(data);
      } catch (error) {
        Logger.error('[PrepayPayment] Prepay.get: ', error);
      }
    };

    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
    getPrepayData();
  }, [$location, Prepay, configStatus, dispatch, token]);

  const addCard = async (nonce) => {
    if (isProcessing) {
      return;
    }
    setErrorMessages([]);
    setIsProcessing(true);

    const payload = {
      ...prepayData.prepay,
      stripe_payment_method_id: nonce,
    };

    try {
      await Prepay.submit(token, payload);
      setIsProcessing(false);
      $location.url(`/prepay/${token}/confirmation`);
    } catch (error) {
      setIsProcessing(false);
      if (error?.status === 400 && error?.data?.code === 'prepaid_card_not_supported') {
        setErrorMessages([getTranslation({
          translationKey: 'prepaid_card_error',
          fallback: "Sorry, we can't accept prepaid cards. Please try another payment method.",
        })]);
      } else {
        Logger.error('[PrepayPayment] ERROR saving card:', error);
        setErrorMessages([
          getTranslation({ translationKey: 'address_form.error_system', fallback: 'A system error has occurred. Please try again later.' }),
          getTranslation({ translationKey: 'prepay_error_persists', fallback: 'If the error persists, please email <a href="mailto:support@resy.com?subject=3rd Party Pre-Pay">support@resy.com</a>.' }),
        ]);
      }
    }
  };

  const handlePaymentSubmit = async (nonce) => {
    addCard(nonce);
  };

  if (!prepayData?.prepay) {
    return <Loader />;
  }

  return (
    <div className="PrepayPayment">
      <div className="PrepayPayment__container">
        <header>
          <div>
            <TranslationProvider translationKey="prepay_payment_method_header">
              <h1>Confirm Payment</h1>
            </TranslationProvider>
            <TranslationProvider translationKey="card_verification_hold_message">
              <p>To verify your card, our payment processor may place a temporary charge on your account, which will be released back to you within a few days.</p>
            </TranslationProvider>
          </div>
        </header>
        <div>
          <div>
            <TranslationProvider translationKey="prepay_legal">
              <p>
                Your credit card information is secure. By clicking &quot;Confirm Purchase&quot; you agree to Resy&apos;s
                <a href="/terms" target="_blank">Terms of Use</a>
                {' '}
                and
                <a href="/privacy" target="_blank">Privacy Policy</a>
                .
              </p>
            </TranslationProvider>
          </div>
          <PrepayStripe
            $rootScope={$rootScope}
            AutomationMode={AutomationMode}
            clientSecret={prepayData.prepay.setup_intent}
            accountId={prepayData.stripe_account_id}
            onSubmit={handlePaymentSubmit}
            stripePublishableKey={stripePublishableKey}
          />

          {errorMessages.map((msg) => <div className="PrepayPayment__error">{msg}</div>)}
        </div>
      </div>
    </div>
  );
};

PrepayPayment.propTypes = prepayPaymentPropTypes;
PrepayPayment.defaultProps = {
  stripePublishableKey: undefined,
};

PrepayPayment.displayName = 'PrepayPayment';

const mapStateToProps = ({ resyConfig }) => ({
  configStatus: resyConfig.configStatus,
  stripePublishableKey: resyConfig.config?.stripe_publishable_key,
});

export { prepayPaymentPropTypes, PrepayPayment as ComponentUnderTest };
export default connect(mapStateToProps)(PrepayPayment);
